// MobileBanner.js
// Bottom sheet banner for mobile devices
// Configure in: src/config/sideBannersConfig.js

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  mobileBannerConfig,
  shouldShowMobileBannerOnPage,
  canShowBanner,
  dismissBanner,
  getSessionShowCount,
  incrementSessionShowCount,
} from '../config/sideBannersConfig';
import '../styles/MobileBanner.css';

const BANNER_ID = 'mobile_promo';

const MobileBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const location = useLocation();

  const { content, displayStyle, dismissible, displayRules } = mobileBannerConfig;

  // Check if banner should show
  const checkAndShowBanner = useCallback(() => {
    // Check if enabled and page rules
    if (!mobileBannerConfig.enabled) return false;
    if (!shouldShowMobileBannerOnPage(location.pathname)) return false;
    
    // Check frequency rules
    const frequency = displayRules.showFrequency;
    if (!canShowBanner(BANNER_ID, frequency)) return false;
    
    // Check max shows per session
    if (displayRules.maxShowsPerSession > 0) {
      const showCount = getSessionShowCount(BANNER_ID);
      if (showCount >= displayRules.maxShowsPerSession) return false;
    }
    
    return true;
  }, [location.pathname, displayRules]);

  // Handle showing the banner
  useEffect(() => {
    if (!checkAndShowBanner()) {
      setIsVisible(false);
      return;
    }

    // Apply show delay
    const delay = displayRules.showDelay || 0;
    
    const timer = setTimeout(() => {
      setIsVisible(true);
      incrementSessionShowCount(BANNER_ID);
    }, delay);

    return () => clearTimeout(timer);
  }, [checkAndShowBanner, displayRules.showDelay]);

  // Handle scroll trigger (if configured)
  useEffect(() => {
    if (!mobileBannerConfig.enabled) return;
    if (displayRules.showAfterScroll <= 0) return;
    if (isVisible) return;

    const handleScroll = () => {
      if (window.scrollY >= displayRules.showAfterScroll && checkAndShowBanner()) {
        setIsVisible(true);
        incrementSessionShowCount(BANNER_ID);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isVisible, displayRules.showAfterScroll, checkAndShowBanner]);

  // Handle dismiss
  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsAnimatingOut(true);
    
    // Wait for animation then hide
    setTimeout(() => {
      setIsVisible(false);
      setIsAnimatingOut(false);
      dismissBanner(BANNER_ID, displayRules.showFrequency);
    }, 300);
  };

  // Don't render if not visible
  if (!isVisible) return null;

  const isImageBanner = content.type === 'image' && content.imageSrc;
  
  const bannerClasses = [
    'mobile-banner',
    `mobile-banner--${displayStyle}`,
    isImageBanner ? 'mobile-banner--image' : '',
    isAnimatingOut ? 'mobile-banner--hiding' : '',
  ].filter(Boolean).join(' ');

  const bannerStyle = isImageBanner 
    ? {} 
    : {
        background: content.backgroundColor || 'linear-gradient(135deg, #3ac693 0%, #2da883 100%)',
        color: content.textColor || '#ffffff',
      };

  // Image banner content
  const imageContent = (
    <img
      src={content.imageSrc}
      alt={content.imageAlt || 'Banner'}
      className="mobile-banner__image"
    />
  );

  // Text banner content
  const textContent = (
    <div className="mobile-banner__content">
      {content.icon && (
        <span className="mobile-banner__icon">{content.icon}</span>
      )}
      <div className="mobile-banner__text">
        <h3 className="mobile-banner__title">{content.title}</h3>
        {content.description && (
          <p className="mobile-banner__description">{content.description}</p>
        )}
      </div>
      {content.link && content.linkText && (
        <Link to={content.link} className="mobile-banner__cta">
          {content.linkText}
        </Link>
      )}
    </div>
  );

  const bannerContent = isImageBanner ? imageContent : textContent;

  // Wrap in link if has link but no explicit CTA button
  if (content.link && !content.linkText) {
    return (
      <div className={bannerClasses} style={bannerStyle}>
        <Link to={content.link} className="mobile-banner__link-wrapper">
          {bannerContent}
        </Link>
        {dismissible && (
          <button
            className="mobile-banner__close"
            onClick={handleDismiss}
            aria-label="Закрыть"
          >
            ×
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={bannerClasses} style={bannerStyle}>
      {bannerContent}
      {dismissible && (
        <button
          className="mobile-banner__close"
          onClick={handleDismiss}
          aria-label="Закрыть"
        >
          ×
        </button>
      )}
    </div>
  );
};

export default MobileBanner;
